import { Box, BoxProps, FormLabel } from "@chakra-ui/react";
import { ReactNode } from "react";
import { FormattedMessage } from "react-intl";

import { RanaLoading } from "@nens/ranalib";

import { useWmsLegendQuery } from "@/queries/query-wms-legend";

import { LegendDiscrete } from "./legend-discrete";
import { LegendGradient } from "./legend-gradient";

type LegendColorStop = {
  value: number;
  color: string;
};

interface WmsLegendProps extends BoxProps {
  descriptorId: string;
  layer: string;
  label?: ReactNode;
  styles?: string;
  unit?: string;
}

interface StopsLegendProps extends BoxProps {
  legend: LegendColorStop[];
  legendId: string;
  label?: ReactNode;
  unit?: string;
}

const RanaLegendLoader = () => <RanaLoading isCentered size="sm" mb={2} />;

function LegendLabel({ label }: { label?: ReactNode }) {
  if (label) return label;

  return (
    <FormLabel overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
      <FormattedMessage id="raster.legend" defaultMessage="Legend" />
    </FormLabel>
  );
}

export function RanaLegend({ descriptorId, layer, label, styles, unit, ...rest }: WmsLegendProps) {
  const legendId = layer + styles;

  const legendResponse = useWmsLegendQuery(descriptorId, layer, styles);

  if (legendResponse.isLoading) return <RanaLegendLoader />;

  if (!legendResponse.isSuccess) return null;

  const { colormap_type, legend, labels } = legendResponse.data;

  return (
    <Box overflow="hidden" {...rest}>
      <LegendLabel label={label} />

      {colormap_type === "gradient" ? (
        <LegendGradient legend={legend} legendId={legendId} unit={unit} withLabel isHorizontal />
      ) : null}
      {colormap_type === "discrete" ? <LegendDiscrete legend={legend} labels={labels} /> : null}
    </Box>
  );
}

function RanaLegendFromStops({ legend, legendId, label, unit, ...rest }: StopsLegendProps) {
  if (!legend.length) return null;

  return (
    <Box overflow="hidden" {...rest}>
      <LegendLabel label={label} />
      <LegendGradient legend={legend} legendId={legendId} unit={unit} withLabel isHorizontal />
    </Box>
  );
}

RanaLegend.Loader = RanaLegendLoader;
RanaLegend.LegendDiscrete = LegendDiscrete;
RanaLegend.LegendGradient = LegendGradient;
RanaLegend.FromStops = RanaLegendFromStops;
